import React, { useEffect, useMemo, useState } from "react";
import { GeoJSON, Popup } from "react-leaflet";
import { fetchDebrisFlowRisk } from "../../lib/api";

const RISK_BORDER = { elevated: "#E63946", advisory: "#F59E0B" };

const fmtWindow = (iso) =>
  new Date(iso).toLocaleString([], { weekday: "short", hour: "numeric" });

export default function ScarRainLayer({ perimeters = [] }) {
  const [scars, setScars] = useState([]);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const load = () =>
      fetchDebrisFlowRisk().then((d) => setScars(d.scars || [])).catch(() => {});
    load();
    const t = setInterval(load, 10 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const shaded = useMemo(() => {
    const geomById = new Map(perimeters.filter((p) => p.geometry).map((p) => [p.id, p.geometry]));
    return scars
      .filter((s) => (s.peak_6h_in || 0) >= 0.01 && geomById.has(s.scar_id))
      .map((s) => ({ ...s, geometry: geomById.get(s.scar_id) }));
  }, [scars, perimeters]);

  const open = shaded.find((s) => s.scar_id === openId);
  const openBorder = open ? RISK_BORDER[open.risk_level] || "#4A9BD1" : null;

  return (
    <>
      {shaded.map((s) => {
        const opacity = Math.min(0.55, 0.12 + (s.peak_6h_in || 0) * 0.8);
        const border = RISK_BORDER[s.risk_level] || "#4A9BD1";
        return (
          <GeoJSON
            key={`rain-${s.scar_id}-${s.computed_at}`}
            data={s.geometry}
            eventHandlers={{
              click: () => setOpenId((prev) => (prev === s.scar_id ? null : s.scar_id)),
            }}
            style={{
              color: border,
              weight: s.risk_level !== "none" ? 2 : 1,
              dashArray: "4 4",
              fillColor: "#4A9BD1",
              fillOpacity: opacity,
            }}
          />
        );
      })}
      {open && open.centroid_lat != null && (
        <Popup
          position={[open.centroid_lat, open.centroid_lon]}
          closeOnClick={false}
          autoClose={false}
          eventHandlers={{ remove: () => setOpenId(null) }}
        >
          <div style={{ fontSize: 12, minWidth: 190 }} data-testid="scar-rain-popup">
            <b>{open.name} burn scar</b> — rain next 48h
            <br />
            up to {open.peak_6h_in}" / 6 hrs · {open.max_pop}% chance · around {fmtWindow(open.window_start)}
            {open.actual_6h_in != null && open.actual_6h_in >= 0.01 && (
              <>
                <br />
                <span data-testid="scar-rain-measured">
                  measured: {open.actual_6h_in}" fell in the last 6 hrs
                  {open.escalated ? " — risk escalated" : ""}
                </span>
              </>
            )}
            {open.risk_level !== "none" && (
              <>
                <br />
                <b style={{ color: openBorder }}>
                  {open.risk_level === "elevated" ? "ELEVATED" : "ADVISORY"} debris-flow risk
                </b>
              </>
            )}
            <div style={{ fontSize: 10, opacity: 0.65, marginTop: 4 }}>
              click the shaded area again to close
            </div>
          </div>
        </Popup>
      )}
    </>
  );
}
